import React from 'react';
import GroupSettingsRow from './GroupSettingsRow';
import { Box, Button, CircularProgress, Divider, TextField, Typography } from '../ui';

interface GroupBrandingTabProps {
  // Controlled — the branding fields live in the page because its
  // full-replace save (buildGroupAccessRequest) bundles them with the
  // seat/access state.
  primaryColor: string;
  setPrimaryColor: (value: string) => void;
  logoPreview: string | null;
  onLogoFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onLogoRemove: () => void;
  saving: boolean;
  onSave: () => void;
}

/**
 * GroupBrandingTab
 *
 * The Branding tab for the group editor: a per-group primary color + logo
 * applied when learners are scoped to this group. Extracted from GroupEdit
 * as a CONTROLLED component (state + save stay in the page).
 */
export default function GroupBrandingTab({
  primaryColor,
  setPrimaryColor,
  logoPreview,
  onLogoFileSelect,
  onLogoRemove,
  saving,
  onSave,
}: GroupBrandingTabProps) {
  return (
    <Box sx={{ maxWidth: 960, display: 'flex', flexDirection: 'column' }}>
      <GroupSettingsRow title="Brand" description="Distinct color and logo applied when learners are scoped to this group.">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <TextField
              label="Primary Color"
              fullWidth
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#3ea6d6"
              helperText="Hex color (e.g., #3ea6d6)"
            />
            {primaryColor && /^#[0-9A-Fa-f]{6}$/.test(primaryColor) && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  mt: '8px',
                  borderRadius: 1,
                  backgroundColor: primaryColor,
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight="bold" sx={{ width: 70, flexShrink: 0 }}>
              Logo
            </Typography>
            <Button variant="outlined" component="label" size="small">
              {logoPreview ? 'Replace' : 'Upload'} Logo
              <input hidden accept="image/*" type="file" onChange={onLogoFileSelect} />
            </Button>
            {logoPreview && (
              <>
                <Box
                  component="img"
                  src={logoPreview}
                  alt="Logo Preview"
                  sx={{
                    maxWidth: 120,
                    maxHeight: 40,
                    objectFit: 'contain',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 0.5,
                  }}
                />
                <Button variant="text" size="small" color="error" onClick={onLogoRemove}>
                  Remove
                </Button>
              </>
            )}
          </Box>
        </Box>
      </GroupSettingsRow>
      <Divider sx={{ mb: 3 }} />
      <Button
        variant="contained"
        onClick={onSave}
        disabled={saving}
        sx={{ alignSelf: 'flex-start', px: 3 }}
      >
        {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Branding'}
      </Button>
    </Box>
  );
}
